import { Operation } from "rfc6902";
import { TypeAheadItem } from "../../controls/ControlTypeAhead";
import { IRequestStatus } from "../../utility";
import {
  AnalysisMethod,
  AnalysisMethodId,
  AnalysisSet,
  AnalysisSetId,
  ConditionComparatorEnum,
  DataSubset,
  DataSubsetId,
  GroupingFactor,
  GroupingFactorId,
} from "../ars_ldm";

/**
 * Represents a variable available for use in where clause conditions.
 * Extends TypeAheadItem to support typeahead functionality.
 */
export interface AnnotationVariable extends TypeAheadItem {
  /** The dataset and variable name combined, e.g., "ADSL.AGE" */
  value: string;
  /** Unique identifier for the variable */
  oid: string;
  /** Name of the variable */
  name: string;
  /** Data type of the variable (e.g., "text", "numeric") */
  dataType: string;
  /** Maximum length for the variable's value */
  length: number;
  /** Dataset containing the variable (e.g., "ADSL") */
  dataset: string;
  /** Code list identifier if the variable uses controlled terminology */
  codeList?: string;
}

/**
 * Single update for PATCH_ARS_STATE operation.
 */
export interface ARSStateUpdate {
  /** JSON Pointer path to the data in ARSContext state */
  dataPath: string;
  /** Value to set at the pointer location */
  value: unknown;
}

/**
 * Core ARS data state without undo/redo metadata.
 */
export interface ARSDataState {
  // Read-only reference data (loaded from backend/hierarchy)
  /** List of variables available for use in where clause conditions */
  variables: AnnotationVariable[];
  /** Available comparators for where clause conditions */
  comparators: ConditionComparatorEnum[];

  // Editable/sharable lists (persisted to backend)
  /** Analysis sets indexed by ID */
  analysisSets: Record<AnalysisSetId, AnalysisSet>;
  /** Data subsets indexed by ID */
  dataSubsets: Record<DataSubsetId, DataSubset>;
  /** Analysis methods indexed by ID */
  analysisMethods: Record<AnalysisMethodId, AnalysisMethod>;
  /** Grouping factors indexed by ID */
  groupingFactors: Record<GroupingFactorId, GroupingFactor>;

  // UI state - for loading/error status
  /** Request status for UI operations */
  uiRequestStatus: IRequestStatus<string | object>;
  changed: boolean; // Optional flag to track unsaved changes
}

/**
 * Represents a patch in the undo/redo stack.
 */
export interface ARSPatch {
  /** Forward patch to apply for redo */
  forward: Operation[];
  /** Reverse patch to apply for undo */
  reverse: Operation[];
}

/**
 * State shape for the ARS context.
 */
export interface ARSContextState extends Record<
  string,
  | boolean
  | number
  | AnnotationVariable[]
  | ConditionComparatorEnum[]
  | Record<string, AnalysisMethod | AnalysisSet | DataSubset | GroupingFactor>
  | IRequestStatus<string | object>
  | ARSPatch[]
> {
  // Read-only reference data (loaded from backend/hierarchy)
  /** List of variables available for use in where clause conditions */
  variables: AnnotationVariable[];
  /** Available comparators for where clause conditions */
  comparators: ConditionComparatorEnum[];

  // Editable/sharable lists (persisted to backend)
  /** Analysis sets indexed by ID */
  analysisSets: Record<AnalysisSetId, AnalysisSet>;
  /** Data subsets indexed by ID */
  dataSubsets: Record<DataSubsetId, DataSubset>;
  /** Analysis methods indexed by ID */
  analysisMethods: Record<AnalysisMethodId, AnalysisMethod>;
  /** Grouping factors indexed by ID */
  groupingFactors: Record<GroupingFactorId, GroupingFactor>;

  // UI state - for loading/error status
  /** Request status for UI operations */
  uiRequestStatus: IRequestStatus<string | object>;
  changed: boolean; // Optional flag to track unsaved changes

  // Undo/redo state
  /** Stack of patches for undo/redo functionality */
  patches: ARSPatch[];
  /** Current index in the patches stack */
  ixPatch: number;
}

/**
 * Context interface combining state and dispatch.
 */
export interface IARSContext {
  /** Current ARS state */
  state: ARSContextState;
  /** Dispatch function for ARS actions */
  dispatch: React.Dispatch<IARSAction>;
}

// Action constants
export const ADD_EXTRA_WHERE_CLAUSE = "ADD_EXTRA_WHERE_CLAUSE";
export const ADD_GROUP = "ADD_GROUP";
export const ADD_NOT_TO_WHERE_CLAUSE = "ADD_NOT_TO_WHERE_CLAUSE";
export const ARS_CHANGE_SENT = "ARS_CHANGE_SENT";
export const LOAD_ANALYSIS_SETS = "LOAD_ANALYSIS_SETS";
export const LOAD_ANALYSIS_METHODS = "LOAD_ANALYSIS_METHODS";
export const LOAD_DATA_SUBSETS = "LOAD_DATA_SUBSETS";
export const LOAD_GROUPING_FACTORS = "LOAD_GROUPING_FACTORS";
export const LOAD_REFERENCE_DATA = "LOAD_REFERENCE_DATA";
export const PATCH_ARS_STATE = "PATCH_ARS_STATE";
export const REDO_ARS_CHANGE = "REDO_ARS_CHANGE";
export const REMOVE_ARS_ITEM = "REMOVE_ARS_ITEM";
export const REMOVE_NOT_FROM_WHERE_CLAUSE = "REMOVE_NOT_FROM_WHERE_CLAUSE";
export const REMOVE_WHERE_CLAUSE = "REMOVE_WHERE_CLAUSE";
export const RESET_STATE = "RESET_STATE";
export const SET_UI_REQUEST_STATUS = "SET_UI_REQUEST_STATUS";
export const UNDO_ARS_CHANGE = "UNDO_ARS_CHANGE";

/**
 * Union type of all action operation strings.
 */
export type ARSOperation =
  | typeof ADD_EXTRA_WHERE_CLAUSE
  | typeof ARS_CHANGE_SENT
  | typeof LOAD_ANALYSIS_METHODS
  | typeof LOAD_ANALYSIS_SETS
  | typeof LOAD_DATA_SUBSETS
  | typeof LOAD_GROUPING_FACTORS
  | typeof LOAD_REFERENCE_DATA
  | typeof ADD_NOT_TO_WHERE_CLAUSE
  | typeof PATCH_ARS_STATE
  | typeof REDO_ARS_CHANGE
  | typeof REMOVE_ARS_ITEM
  | typeof REMOVE_NOT_FROM_WHERE_CLAUSE
  | typeof REMOVE_WHERE_CLAUSE
  | typeof RESET_STATE
  | typeof SET_UI_REQUEST_STATUS
  | typeof UNDO_ARS_CHANGE;

/**
 * Base interface for all ARS actions.
 */
export interface IARSActionBase {
  operation: ARSOperation;
}

export interface IARSActionAddExtraWhereClause extends IARSActionBase {
  operation: typeof ADD_EXTRA_WHERE_CLAUSE;
  /** Data path of the entity containing the condition or compoundExpression */
  dataPath: string;
  newLength: number;
}

export interface IARSActionChangeSent extends IARSActionBase {
  operation: typeof ARS_CHANGE_SENT;
}

export interface IARSActionLoadAnalysisMethods extends IARSActionBase {
  operation: typeof LOAD_ANALYSIS_METHODS;
  analysisMethods: AnalysisMethod[];
}
export interface IARSActionLoadAnalysisSets extends IARSActionBase {
  operation: typeof LOAD_ANALYSIS_SETS;
  analysisSets: AnalysisSet[];
}
export interface IARSActionLoadDataSubsets extends IARSActionBase {
  operation: typeof LOAD_DATA_SUBSETS;
  dataSubsets: DataSubset[];
}
export interface IARSActionLoadGroupingFactors extends IARSActionBase {
  operation: typeof LOAD_GROUPING_FACTORS;
  groupingFactors: GroupingFactor[];
}
export interface IARSActionLoadReferenceData extends IARSActionBase {
  operation: typeof LOAD_REFERENCE_DATA;
  variables: AnnotationVariable[];
  comparators: ConditionComparatorEnum[];
}

export interface IARSActionNotWhereClause extends IARSActionBase {
  operation: typeof ADD_NOT_TO_WHERE_CLAUSE;
  /** Data path of the where clause to negate */
  dataPath: string;
}

export interface IARSActionPatchARSStateSingle extends IARSActionBase, ARSStateUpdate {
  operation: typeof PATCH_ARS_STATE;
}
export interface IARSActionPatchARSStateMultiple extends IARSActionBase {
  operation: typeof PATCH_ARS_STATE;
  /** Array of updates to apply atomically */
  updates: ARSStateUpdate[];
}

export interface IARSActionRemoveItem extends IARSActionBase {
  operation: typeof REMOVE_ARS_ITEM;
  dataPath: string;
  newLength?: number;
}
export interface IARSActionRemoveNotFromWhereClause extends IARSActionBase {
  operation: typeof REMOVE_NOT_FROM_WHERE_CLAUSE;
  /** Data path of the where clause to remove NOT from */
  dataPath: string;
}
export interface IARSActionRemoveWhereClause extends IARSActionBase {
  operation: typeof REMOVE_WHERE_CLAUSE;
  /** Data path of the entity containing the compoundExpression */
  dataPath: string;
  /** The id of the SubClause to remove */
  id: string;
}
export interface IARSActionSetUIRequestStatus extends IARSActionBase {
  operation: typeof SET_UI_REQUEST_STATUS;
  uiRequestStatus: IRequestStatus<string | object>;
}

export interface IARSActionUndoChange extends IARSActionBase {
  operation: typeof UNDO_ARS_CHANGE;
  /** Number of steps to undo, defaults to 1 */
  steps?: number;
}

export interface IARSActionRedoChange extends IARSActionBase {
  operation: typeof REDO_ARS_CHANGE;
  /** Number of steps to redo, defaults to 1 */
  steps?: number;
}

/**
 * Union type of all possible ARS actions.
 */
export type IARSAction =
  | IARSActionAddExtraWhereClause
  | IARSActionChangeSent
  | IARSActionLoadAnalysisMethods
  | IARSActionLoadAnalysisSets
  | IARSActionLoadDataSubsets
  | IARSActionLoadGroupingFactors
  | IARSActionLoadReferenceData
  | IARSActionNotWhereClause
  | IARSActionPatchARSStateSingle
  | IARSActionPatchARSStateMultiple
  | IARSActionRedoChange
  | IARSActionRemoveItem
  | IARSActionRemoveNotFromWhereClause
  | IARSActionRemoveWhereClause
  | IARSActionSetUIRequestStatus
  | IARSActionUndoChange;
